/**
 * DirectionArrows — overlay that paints small directional chevrons
 * along a polyline so the user can see direction-of-travel at a glance.
 *
 * Originally added to the trip viewer (b98.3, field test 2026-05-04).
 * b99.9.31 (2026-05-15) — refactored to be **zoom-aware** so the
 * arrow density automatically tracks the current map zoom level
 * (zoom-in → more arrows, zoom-out → fewer). User feedback that the
 * fixed-interval rendering looked sparse on detail views and
 * cluttered on country-wide views.
 *
 * Design choices:
 *
 *   • In-house, no extra deps. `leaflet-polylinedecorator` and
 *     `react-leaflet-arrowheads` both target react-leaflet v3/v4 and
 *     are abandoned for v5 (which we use). What we need is trivial:
 *     walk the path by km, render rotated div-icons.
 *
 *   • Single source of truth: this is the ONE component for arrows.
 *     Editor / Trip Viewer / Public map all import it. Earlier the
 *     editor had its own local copy that drifted in size and color.
 *
 *   • Color defaults to a high-contrast slate that reads on any
 *     polyline color and on light/dark map tiles. We deliberately do
 *     NOT derive the arrow color from the polyline color — the whole
 *     point is contrast so the user spots direction without having
 *     to squint.
 */
import { useContext, useEffect, useMemo, useState } from "react";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { MapArrowsContext } from "../maps/MapArrowsToggle";

/**
 * Cumulative-distance walk over `coords` to pick the index closest to
 * each multiple of `intervalKm`. Returns an array of indexes into
 * `coords`. Cheaper than re-scanning per arrow because we only walk
 * the array once.
 */
function pickIndexesByInterval(coords, intervalKm) {
  if (!coords || coords.length < 2) return [];
  const out = [];
  let acc = 0;
  let nextThreshold = intervalKm;
  for (let i = 1; i < coords.length; i++) {
    const dKm = haversineKm(
      coords[i - 1][0], coords[i - 1][1],
      coords[i][0], coords[i][1],
    );
    acc += dKm;
    if (acc >= nextThreshold) {
      out.push(i);
      nextThreshold += intervalKm;
    }
  }
  return out;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

/**
 * Initial bearing in degrees from (lat1,lon1) to (lat2,lon2).
 * 0 = north, 90 = east. Matches the `transform: rotate()` CSS
 * convention so the arrow points along the path of travel.
 */
function bearingDeg(lat1, lon1, lat2, lon2) {
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const λ1 = lon1 * Math.PI / 180;
  const λ2 = lon2 * Math.PI / 180;
  const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Map zoom level → arrow interval in kilometres. Inversely
 * proportional: low zoom (country view) ≈ large interval, high zoom
 * (street view) ≈ small interval.
 *
 * Tuned by eye on the orange OSM tiles MotoTrips uses. At the chosen
 * intervals, a typical day-ride route renders 10-25 visible arrows
 * across the range of zooms a user would actually inspect at.
 *
 * Capped on both ends so:
 *   - We never get a chevron every 50 m at zoom 18 (would block the
 *     polyline entirely on a city block).
 *   - We never get a single arrow on a country-wide view at zoom 5
 *     (point of arrows is to be visible at a glance).
 */
function intervalKmForZoom(zoom) {
  if (zoom >= 16) return 0.4;
  if (zoom >= 15) return 0.7;
  if (zoom >= 14) return 1.2;
  if (zoom >= 13) return 2.0;
  if (zoom >= 12) return 3.5;
  if (zoom >= 11) return 6.0;
  if (zoom >= 10) return 10.0;
  if (zoom >= 9)  return 18.0;
  if (zoom >= 8)  return 30.0;
  if (zoom >= 7)  return 50.0;
  return 80.0;
}

/**
 * Build a Leaflet `divIcon` that draws a chevron rotated by `angle`.
 * Cached by (angle rounded to 5°, color, size) so we don't allocate
 * hundreds of icon objects on long routes.
 */
const iconCache = new Map();
function getArrowIcon(angle, color, stroke, size) {
  const rounded = Math.round(angle / 5) * 5;
  const key = `${rounded}_${color}_${stroke}_${size}`;
  if (iconCache.has(key)) return iconCache.get(key);
  // Chevron path — slightly fatter than the v98 arrow, tail tucked
  // in so the rotation pivot reads as the arrow centre.
  const icon = L.divIcon({
    className: "trip-direction-arrow",
    html: `<div style="
      width:${size}px;height:${size}px;
      transform:rotate(${rounded}deg);
      transform-origin:center;
      display:flex;align-items:center;justify-content:center;
      pointer-events:none;
      ">
      <svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="${color}" stroke="${stroke}" stroke-width="2" stroke-linejoin="round">
        <path d="M12 2 L20 19 L12 15 L4 19 Z" />
      </svg>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
  iconCache.set(key, icon);
  return icon;
}

/**
 * @param {Array<[number, number]>} coords        - polyline points [lat, lon]
 * @param {string} [color="#0f172a"]              - chevron fill (slate-900 by default; high contrast vs polyline)
 * @param {string} [stroke="#ffffff"]             - chevron outline
 * @param {number} [size=24]                      - icon size in px
 * @param {number} [intervalKm]                   - fixed interval; if omitted, zoom-aware
 * @param {boolean} [compact=false]               - smaller chevrons & sparser interval for inline maps (b99.9.67)
 */
export default function DirectionArrows({
  coords,
  color = "#0f172a",
  stroke = "#ffffff",
  size = 24,
  intervalKm,
  compact = false,
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => (map ? map.getZoom() : 10));
  // b99.9.36 — honour the per-map MapArrowsContext so a single
  // toggle button can hide every arrow chain on the map at once.
  // Default value is `true` so call sites without a provider keep
  // working unchanged.
  const arrowsVisible = useContext(MapArrowsContext);

  // Subscribe to zoom changes so we re-pick the arrow indexes when
  // the user zooms in/out. `zoomend` is fired once per gesture so
  // we're not thrashing the array during the animation.
  useMapEvents({
    zoomend: () => {
      if (map) setZoom(map.getZoom());
    },
  });

  // Keep state in sync if the map instance changes (e.g. fullscreen
  // toggle remounts the MapContainer in a portal).
  useEffect(() => {
    if (map) setZoom(map.getZoom());
  }, [map]);

  const effectiveInterval = useMemo(() => {
    if (typeof intervalKm === "number" && intervalKm > 0) return intervalKm;
    // b99.9.67 — `compact` doubles the spacing so inline trip-sheet
    // maps render ~half the chevrons (less visual noise on the small
    // 250-px canvas; fullscreen keeps the original density).
    const base = intervalKmForZoom(zoom);
    return compact ? base * 2 : base;
  }, [intervalKm, zoom, compact]);

  // b99.9.67/.87 — Compact mode shrinks the chevron icons to match
  // the smaller dot waypoint pins. b99.9.87 bumped 14→18 because the
  // 14 px chevrons read too small alongside the rest of the map UI.
  const effectiveSize = compact ? 18 : size;

  const idxs = useMemo(
    () => pickIndexesByInterval(coords, effectiveInterval),
    [coords, effectiveInterval],
  );

  if (!coords || coords.length < 2) return null;
  if (!arrowsVisible) return null;

  return (
    <>
      {idxs.map((i) => {
        const [lat, lon] = coords[i];
        const [latPrev, lonPrev] = coords[i - 1];
        const angle = bearingDeg(latPrev, lonPrev, lat, lon);
        return (
          <Marker
            key={`arrow-${i}-${effectiveInterval}-${effectiveSize}`}
            position={[lat, lon]}
            icon={getArrowIcon(angle, color, stroke, effectiveSize)}
            interactive={false}
            keyboard={false}
            // Leaflet's `overlayPane` (where <Polyline> lives) sits
            // BELOW `markerPane`. Putting arrows on overlayPane would
            // hide them under the route line — flip to markerPane so
            // they ride visibly on top. Still non-interactive so they
            // don't intercept clicks meant for the labelled pins.
            pane="markerPane"
          />
        );
      })}
    </>
  );
}
